"use client";

import { Link } from "@/i18n/Link";
import { trackContentClick } from "@/lib/analytics/trackContentClick";
import { sbLink } from "@/lib/storyblok/link";
import type { ReactNode } from "react";
import type { StoryblokMultilink } from "storyblok";
import styles from "./MediaLink.module.css";

interface MediaLinkProps {
	/** The `mediaLink` field from Storyblok. Renders `children` untouched when empty. */
	link?: unknown;
	contentType: string;
	itemId: string;
	label?: string;
	children: ReactNode;
}

/**
 * Wraps a blok's media in a tracked link. Only the media is wrapped (never the whole blok),
 * so buttons and other links in the blok's content never end up nested inside an anchor.
 */
export const MediaLink = ({ link, contentType, itemId, label, children }: MediaLinkProps) => {
	const linkData = link ? sbLink(link as StoryblokMultilink) : null;

	if (!linkData?.href) return <>{children}</>;

	return (
		<Link
			href={linkData.href}
			className={styles.root}
			{...(linkData.external && { target: linkData.target, rel: "noopener noreferrer" })}
			onClick={() => trackContentClick({ contentType, itemId, href: linkData.href, label })}
		>
			{children}
		</Link>
	);
};
